import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";
import {
  Search, Users, Stethoscope, Calendar, FileText, Receipt, CreditCard, UsersRound, Loader2,
} from "lucide-react";

const GROUPS = [
  { key: "patients", label: "Patients", icon: Users, route: (r) => `/patients/${r.patient_id}`, render: (r) => ({ title: r.name, sub: `${r.age || "?"}y · ${r.gender || "?"} · ${r.phone || ""}` }) },
  { key: "doctors", label: "Doctors", icon: Stethoscope, route: () => `/team`, render: (r) => ({ title: r.name, sub: `${r.role} · ${r.email}` }) },
  { key: "staff", label: "Staff", icon: UsersRound, route: () => `/team`, render: (r) => ({ title: r.name, sub: `${r.role} · ${r.email}` }) },
  { key: "appointments", label: "Appointments", icon: Calendar, route: () => `/appointments`, render: (r) => ({ title: r.patient_name, sub: `${r.date_time?.slice(0, 16)?.replace("T", " ") || ""} · Dr. ${r.doctor_name || ""} · ${r.status}` }) },
  { key: "prescriptions", label: "Prescriptions", icon: FileText, route: () => `/patients`, render: (r) => ({ title: r.patient_name, sub: `${r.diagnosis || "—"} · ${(r.date || "").slice(0, 10)}` }) },
  { key: "invoices", label: "Invoices", icon: Receipt, route: () => `/billing`, render: (r) => ({ title: r.patient_name, sub: `₹${r.amount} · ${r.status}` }) },
  { key: "payments", label: "Payments", icon: CreditCard, route: () => `/billing`, render: (r) => ({ title: r.plan_id?.toUpperCase() || "Payment", sub: `${r.status} · ₹${r.amount}` }) },
];

export default function SearchPalette({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Reset on open
  useEffect(() => {
    if (open) {
      setQ("");
      setData(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setData(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term, limit: 5 } });
        setData(data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const goTo = useCallback((g, r) => {
    onOpenChange(false);
    navigate(g.route(r));
  }, [navigate, onOpenChange]);

  const totalCount = data?.counts ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden"
        data-testid="search-palette"
        aria-describedby={undefined}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search patients, doctors, appointments, invoices…"
            className="flex-1 outline-none text-base placeholder:text-slate-400 bg-transparent"
            data-testid="search-input"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase font-mono text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-2 py-2" data-testid="search-results">
          {!data && q.trim().length >= 2 && !loading && (
            <div className="text-center py-10 text-sm text-slate-400">Searching…</div>
          )}
          {!data && q.trim().length < 2 && (
            <div className="text-center py-10 text-sm text-slate-400">
              Type at least 2 characters to search across patients, appointments, invoices and more.
            </div>
          )}
          {data && totalCount === 0 && (
            <div className="text-center py-10 text-sm text-slate-400" data-testid="search-no-results">
              No results for &ldquo;{q}&rdquo;
            </div>
          )}
          {data && totalCount > 0 && GROUPS.map((g) => {
            const items = data.results[g.key] || [];
            if (items.length === 0) return null;
            const Icon = g.icon;
            return (
              <div key={g.key} className="mb-2" data-testid={`group-${g.key}`}>
                <div className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-400 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" /> {g.label} ({items.length})
                </div>
                <div className="space-y-0.5">
                  {items.map((r, idx) => {
                    const v = g.render(r);
                    const id = r.patient_id || r.user_id || r.appointment_id || r.prescription_id || r.invoice_id || r.payment_session_id || idx;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => goTo(g, r)}
                        className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-100 transition-colors"
                        data-testid={`search-result-${g.key}-${idx}`}
                      >
                        <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">{v.title}</div>
                          <div className="text-xs text-slate-500 truncate">{v.sub}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
